import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, Building2, CheckCircle, HelpCircle } from 'lucide-react';

const Pricing = () => {
  const pricingCards = [
    {
      icon: Home,
      title: 'Residential Cleaning',
      price: 'Starting from $40',
      unit: 'per hour',
      description: 'Perfect for homes, apartments, and residential properties',
      features: [
        'Regular or deep cleaning',
        'Flexible scheduling',
        'Eco-friendly products',
        'Trained professionals',
        'Satisfaction guarantee',
      ],
    },
    {
      icon: Building2,
      title: 'Commercial Cleaning',
      price: 'Starting from $40',
      unit: 'per hour',
      description: 'Ideal for offices, retail spaces, and commercial properties',
      features: [
        'After-hours availability',
        'Customized cleaning plans',
        'Commercial-grade equipment',
        'Insured and bonded',
        'Regular quality checks',
      ],
    },
  ];

  const factors = [
    {
      icon: HelpCircle,
      title: 'Size',
      description: 'Larger properties require more time and resources',
    },
    {
      icon: HelpCircle,
      title: 'Condition',
      description: 'Properties requiring deep cleaning may cost more',
    },
    {
      icon: HelpCircle,
      title: 'Frequency',
      description: 'Regular bookings often receive discounted rates',
    },
    {
      icon: HelpCircle,
      title: 'Extras',
      description: 'Additional services like carpet or window cleaning',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section data-testid="pricing-hero" className="py-16 md:py-24 bg-gradient-to-br from-secondary/30 to-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Transparent Pricing
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            No hidden fees, no surprises. Just honest, competitive pricing for professional cleaning services in Perth.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section data-testid="pricing-cards" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  data-testid={`pricing-card-${index}`}
                  className="bg-white rounded-2xl border-2 border-border p-8 md:p-10 shadow-soft hover:shadow-hover transition-all"
                >
                  <div className="mb-6">
                    <Icon className="w-12 h-12 text-primary mb-4" />
                    <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                      {card.title}
                    </h2>
                    <p className="text-muted-foreground">{card.description}</p>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-baseline mb-2">
                      <span className="text-5xl font-bold text-primary">
                        {card.price}
                      </span>
                    </div>
                    <p className="text-lg text-muted-foreground">{card.unit}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {card.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    data-testid={`pricing-card-${index}-btn`}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6"
                  >
                    <Link to="/#quote-form">Get a Quote</Link>
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              * Prices are GST-free and subject to change. Contact us for a customized quote.
            </p>
          </div>
        </div>
      </section>

      {/* What Affects Price */}
      <section data-testid="price-factors" className="py-16 md:py-24 bg-surface">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              What Affects the Price?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Several factors influence the final cost of your cleaning service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {factors.map((factor, index) => {
              const Icon = factor.icon;
              return (
                <div
                  key={index}
                  data-testid={`price-factor-${index}`}
                  className="bg-white rounded-xl p-6 border border-border text-center"
                >
                  <Icon className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {factor.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {factor.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="pricing-cta" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Request a free quote and we'll provide a detailed estimate based on your specific needs
          </p>
          <Button
            asChild
            data-testid="pricing-cta-btn"
            className="rounded-full bg-primary hover:bg-primary/90 text-white px-12 py-6 text-lg"
          >
            <Link to="/#quote-form">Get Your Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
